import { useState, type SubmitEvent } from 'react';

export interface TotpChallengeLabels {
  codeLabel: string;
  codePlaceholder: string;
  recoveryLabel: string;
  recoveryPlaceholder: string;
  verify: string;
  useRecovery: string;
  useAuthenticator: string;
  rememberDevice?: string;
}

export interface TotpChallengeFormProps {
  methods: string[];
  labels: TotpChallengeLabels;
  onSubmit: (method: 'totp' | 'recovery', code: string, rememberDevice?: boolean) => void;
  isPending?: boolean;
  error?: string | null;
  rememberDevice?: boolean;
  onRememberDeviceChange?: (value: boolean) => void;
}

// Presentational second-factor entry. Controlled entirely by props + callbacks;
// no fetch/i18n/DOM-global coupling (bundled by Wails apps too).
export const TotpChallengeForm = ({
  methods,
  labels,
  onSubmit,
  isPending,
  error,
  rememberDevice,
  onRememberDeviceChange,
}: TotpChallengeFormProps) => {
  const canRecover = methods.includes('recovery');
  const [mode, setMode] = useState<'totp' | 'recovery'>('totp');
  const [code, setCode] = useState('');

  const isRecovery = mode === 'recovery';

  const handleSubmit = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Only append the 3rd arg when the remember-device feature is actually in
    // play, so existing 2-arg callers keep getting exactly 2 arguments
    // (an explicit `undefined` 3rd arg is a different call shape for
    // strict-equality assertions like `toHaveBeenCalledWith`).
    if (typeof rememberDevice === 'boolean') {
      onSubmit(mode, code.trim(), rememberDevice);
    } else {
      onSubmit(mode, code.trim());
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="field">
        <label className="label" htmlFor="mfa-code">
          {isRecovery ? labels.recoveryLabel : labels.codeLabel}
        </label>
        <div className="control">
          <input
            id="mfa-code"
            className="input"
            type="text"
            inputMode={isRecovery ? 'text' : 'numeric'}
            autoComplete="one-time-code"
            autoFocus
            placeholder={isRecovery ? labels.recoveryPlaceholder : labels.codePlaceholder}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </div>
      </div>

      {error && <p className="help is-danger">{error}</p>}

      {onRememberDeviceChange && labels.rememberDevice && (
        <div className="field">
          <label className="checkbox">
            <input
              type="checkbox"
              checked={!!rememberDevice}
              onChange={(e) => onRememberDeviceChange(e.target.checked)}
            />{' '}
            {labels.rememberDevice}
          </label>
        </div>
      )}

      <div className="field is-grouped is-justify-content-space-between">
        <div className="control">
          <button type="submit" className="button is-success" disabled={isPending}>
            {labels.verify}
          </button>
        </div>
        {canRecover && (
          <div className="control">
            <button
              type="button"
              className="button is-ghost"
              onClick={() => {
                setMode(isRecovery ? 'totp' : 'recovery');
                setCode('');
              }}
            >
              {isRecovery ? labels.useAuthenticator : labels.useRecovery}
            </button>
          </div>
        )}
      </div>
    </form>
  );
};
